/** Class that setups a touch / mouse down that resets itself after too long */
import Config from 'config/settings';
import { Module } from 'core/Module';
import { ZoomConfig } from 'app/module/work/WorkTilePixi';
import fastdom from 'fastdom';
import { Events } from 'core/events/Events';

export class HitArea extends Module {
	public config: any = {
		onUpdate: null,
		onCancel: null,
		onComplete: null,
		xTolerence: 30,
		yTolerence: 30,
		startAmount: 15,
		completeAmount: 55,
		slug: 'market-masters',
		title: '',
		index: 0,
		href: '#'
	};

	private _rect: DOMRect;
	private _mouseIsDown: boolean;
	private _isIntersecting: boolean;
	private _mouseDownCords: any;
	private _tickCount: number = 0;
	private _active: boolean = false;
	private _clickable: boolean = false;

	create(): void {}

	public get mouseIsDown(): boolean {
		return this._mouseIsDown;
	}

	public get data(): any {
		return this.config;
	}

	public set clickable(v: boolean) {
		if (!this.$container || !this.DOM) {
			return;
		}

		this._clickable = v;
		this.DOM.toggleClass(this.$container as HTMLElement, '-clickable', this._clickable);

		Events.on('click', this.$container as HTMLElement, (e) => {
			e.preventDefault();
			this.config.onComplete(this.data);
		});
	}

	public set projectActive(v: boolean) {
		if (!this.$container || !this.DOM) {
			return;
		}

		this.DOM.toggleClass(this.$container as HTMLElement, '-projectactive', v);
	}

	public show(): void {
		if (!this.$container) {
			return;
		}
		this._active = true;
		fastdom.mutate(() => {
			(this.$container as HTMLElement).style.visibility = 'visible';
		});
	}

	public hide(): void {
		if (!this.$container) {
			return;
		}

		this._active = false;
		fastdom.mutate(() => {
			(this.$container as HTMLElement).style.visibility = 'hidden';
		});
	}

	public checksIsIntersecting(cords: any, canDelayMeasure = false): boolean {
		if (!this._active) {
			return false;
		}

		this._checkIsIntersecting(cords, canDelayMeasure);
		return this._isIntersecting;
	}

	public mouseUp(): void {
		this._reset();
	}

	/** Update mouse / touchmove cords */
	public updateMouseCords(cords: any): void {
		if (!this._active) {
			return;
		}

		this._checkIsIntersecting(cords);
		if (this._mouseIsDown && this._isIntersecting) {
			this._update();
			this._checkComplete();
			this._checkMoveTolerence(cords);
		}
	}

	/** Set x/y/scale of hit area */
	public setPosition(x: number, y: string, scale: number): void {
		fastdom.mutate(() => {
			(this.$container as HTMLElement).style.transform = `translate3d(${x}px, ${y}, 0) scale(1)`;
		});

	}

	public resizeArea(width: number, height: number): void {
		fastdom.mutate(() => {
			(this.$container as HTMLElement).style.width = `${width}px`;
			(this.$container as HTMLElement).style.height = `${height}px`;
		});
	}

	public zoomToProject(instant: boolean = false, config: ZoomConfig, delay: number = 0.0): void {
		gsap.TweenMax.to(this.$container, 0.7, {
			scaleX: config.maskScaleX,
			scaleY: config.maskScaleY,
			ease: Config.standardEase,
			delay: delay
		});
	}

	//Private
	//---------------------------------------------------------------------

	/** Check if cords are intersecting with this element */
	private _checkIsIntersecting(cords: any, canDelayMeasure = false): void {
		if (canDelayMeasure) {
			fastdom.measure(() => {
				this._rect = this.$container.getBoundingClientRect() as DOMRect;
			});
		} else {
			this._rect = this.$container.getBoundingClientRect() as DOMRect;
		}

		if (!this._rect) {
			return;
		}

		this._isIntersecting =
			this._rect.x <= cords.x &&
			cords.x <= this._rect.x + this._rect.width &&
			this._rect.y <= cords.y &&
			cords.y <= this._rect.y + this._rect.height;
	}

	/** Check if the user moves too far (and therefor resets the action) */
	private _checkMoveTolerence(cords: any): void {
		if (!this._mouseIsDown) {
			return;
		}

		if (
			Math.abs(cords.x - this._mouseDownCords.x) > this.config.xTolerence ||
			Math.abs(cords.y - this._mouseDownCords.y) > this.config.yTolerence
		) {
			this._reset();
			if (this.config.onCancel) {
				this.config.onCancel();
			}
		}
	}

	/** Update */
	private _update(): void {
		this._tickCount += 1;
		if (this.config.onUpdate && this._tickCount > this.config.startAmount) {
			this.config.onUpdate({
				count: this._tickCount,
				ratio: Math.min(
					1,
					(this._tickCount - this.config.startAmount) / (this.config.completeAmount - this.config.startAmount)
				)
			});
		}
	}

	/** check if the click is complete */
	private _checkComplete(): void {
		if (this._tickCount >= this.config.completeAmount) {
			this._reset();
			if (this.config.onComplete) {
				this.config.onComplete(this.config);
			}
		}
	}

	/* reset */
	private _reset(): void {
		this._tickCount = 0;
		this._mouseIsDown = false;
		this._isIntersecting = false;
		this._mouseDownCords = null;
	}
}
